import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getCalendarBookings } from '../../lib/bookings';
import { getBookingFeedback } from '../../lib/feedback';
import { authStateManager } from '../../lib/authStateManager';
import LoadingScreen from '../../components/LoadingScreen';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingStage, setLoadingStage] = useState<string>('');
  const [showLoginAnimation, setShowLoginAnimation] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          navigate('/', { replace: true });
          return;
        }
      } catch (err) {
        console.error('Auth check error:', err);
        setError(err instanceof Error ? err.message : 'Failed to check authentication status');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Listen for auth state changes using centralized manager
    if (!supabase) {
      console.error('Supabase client not initialized');
      setIsLoading(false);
      return;
    }
    
    const unsubscribe = authStateManager.subscribe((_event, session) => {
      if (session?.user) {
        // Check if enhanced login is handling navigation
        const enhancedLoginInProgress = localStorage.getItem('enhancedLoginInProgress');
        if (enhancedLoginInProgress === 'true') {
          console.log('🚀 Auth state change detected, but enhanced login is handling navigation');
          // Clean up the flag and let enhanced login handle the navigation
          localStorage.removeItem('enhancedLoginInProgress');
          return;
        }
        
        // Only navigate if we're still on the login page
        if (window.location.pathname === '/login') {
          console.log('🚀 Auth state change - navigating to home');
          navigate('/', { replace: true });
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [navigate]);

  const validateForm = () => {
    const errors = {
      email: '',
      password: ''
    };

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Only validate that password is not empty
    if (!password.trim()) {
      errors.password = 'Password is required'; 
    }

    setValidationErrors(errors);
    return !errors.email && !errors.password;
  };

  const preloadCalendarData = async (selectedBusiness: any) => {
    if (!supabase) throw new Error('Supabase client not initialized');

    try {
      setLoadingStage('Building workflows.....');
      console.log('🗓️ Preloading calendar data for seamless experience...');

      // Calculate current month date range
      const today = new Date();
      const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      currentMonthStart.setHours(0, 0, 0, 0);
      
      const currentMonthEnd = new Date(currentMonthStart);
      currentMonthEnd.setMonth(currentMonthEnd.getMonth() + 1);
      currentMonthEnd.setDate(0);
      currentMonthEnd.setHours(23, 59, 59, 999);

      console.log('🗓️ Date range:', { 
        start: currentMonthStart.toISOString(), 
        end: currentMonthEnd.toISOString() 
      });

      // Load calendar bookings (location bookings for CalendarV2)
      const calendarBookings = await getCalendarBookings(
        selectedBusiness.id,
        currentMonthStart,
        currentMonthEnd,
        true, // isLocationBooking = true for CalendarV2
        undefined // No resource filtering during preload
      );

      console.log(`🗓️ Loaded ${calendarBookings.length} calendar bookings`);

      // Load time off requests for the same date range
      const { data: timeOffData } = await supabase
        .from('time_off_requests')
        .select(`
          id,
          start_date,
          end_date,
          request_type,
          is_partial_day,
          start_time,
          end_time,
          resource_id,
          resources:resource_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'approved')
        .lte('start_date', currentMonthEnd.toISOString().split('T')[0])
        .gte('end_date', currentMonthStart.toISOString().split('T')[0]);

      console.log(`🗓️ Loaded ${timeOffData?.length || 0} time off requests`);

      // Calculate total sales for current month
      setLoadingStage('Creating Environment.....');
      const formattedMonthStart = currentMonthStart.toISOString().split('T')[0];
      const formattedMonthEnd = currentMonthEnd.toISOString().split('T')[0];

      const { data: salesData, error: salesError } = await supabase
        .from('bookings')
        .select('id, booking_number, total_amount, tax_amount, subtotal, end_date, mobile_booking')
        .eq('business_id', selectedBusiness.id)
        .eq('mobile_booking', false) // Only location bookings
        .gte('end_date', formattedMonthStart)
        .lte('end_date', formattedMonthEnd);

      let totalSales = 0;
      if (!salesError && salesData) {
        totalSales = salesData.reduce((acc, booking) => {
          if (booking.subtotal !== null && booking.subtotal !== undefined) {
            return acc + booking.subtotal;
          }
          const taxAmount = booking.tax_amount || 0;
          const totalAmount = booking.total_amount || 0;
          return acc + (totalAmount - taxAmount);
        }, 0);
      }

      console.log(`💰 Calculated total sales: $${totalSales}`);

      // Load feedback for completed bookings
      setLoadingStage('Analyzing Data.........');
      const completedBookings = calendarBookings.filter(
        booking => booking.extendedProps.status === 'completed'
      );

      const feedbackMap = new Map();
      if (completedBookings.length > 0) {
        await Promise.all(
          completedBookings.map(async (booking) => {
            try {
              const feedback = await getBookingFeedback(booking.id);
              if (feedback) {
                // Calculate average rating
                let averageRating = 0;
                let ratingCount = 0;
                
                if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
                  Object.values(feedback.form_responses).forEach(value => {
                    if (typeof value === 'number' && value > 0) {
                      averageRating += value;
                      ratingCount++;
                    }
                  });
                } else {
                  if (feedback.overall_quality_rating) {
                    averageRating += feedback.overall_quality_rating;
                    ratingCount++;
                  }
                  if (feedback.overall_experience_rating) {
                    averageRating += feedback.overall_experience_rating;
                    ratingCount++;
                  }
                }
                
                if (ratingCount > 0) {
                  averageRating = averageRating / ratingCount;
                  feedbackMap.set(booking.id, {
                    rating: averageRating,
                    hasText: !!(feedback.improvement_suggestions || 
                              (feedback.form_responses && Object.values(feedback.form_responses).some(v => typeof v === 'string' && v.trim())))
                  });
                }
              }
            } catch (error) {
              console.error('Error loading feedback for booking:', booking.id, error);
            }
          })
        );
      }

      console.log(`💬 Loaded feedback for ${feedbackMap.size} completed bookings`);

      // Store all calendar data for instant access
      const calendarData = {
        bookings: calendarBookings,
        totalSales,
        bookingFeedback: Array.from(feedbackMap.entries()),
        currentMonthStart: currentMonthStart.toISOString(),
        currentMonthEnd: currentMonthEnd.toISOString(),
        initialDate: today.toISOString(), // For scroll positioning
        timestamp: Date.now()
      };

      localStorage.setItem('preloadedCalendarData', JSON.stringify(calendarData));
      console.log('🗓️ Calendar data preloaded and stored successfully');

    } catch (error) {
      console.error('❌ Error preloading calendar data:', error);
      // Don't fail the login if calendar preloading fails
      // The calendar will fall back to normal loading
    }
  };

  const determineHomeScreen = async (businessId: string) => {
    if (!supabase) throw new Error('Supabase client not initialized');

    try {
      setLoadingStage('Creating Environment.....');

      // Try direct RPC first for better reliability
      const { data: directData, error: directError } = await supabase.rpc(
        'get_home_screen_direct',
        { business_id_param: businessId }
      );

      if (!directError && directData && directData.home_screen) {
        const targetRoute = directData.home_screen === 'mobile_calendar' ? '/mobile-calendar' : '/calendarV2';
        console.log('Route determined via direct query:', targetRoute);
        return targetRoute;
      }
    } catch (error) {
      console.log('Direct query failed, trying regular query...');
    }

    // Fallback to regular table query
    const { data: settings, error: settingsError } = await supabase
      .from('application_settings')
      .select('*')
      .eq('business_id', businessId);

    if (!settingsError && settings && settings.length > 0) {
      const setting = settings[0];
      const targetRoute = setting.home_screen === 'mobile_calendar' ? '/mobile-calendar' : '/calendarV2';
      console.log('Route determined via regular query:', targetRoute);
      return targetRoute;
    }

    // Default fallback
    console.log('No settings found, using default route');
    return '/calendarV2';
  };

  const loadBusinessesAndDetermineRoute = async (userId: string): Promise<string | null> => {
    if (!supabase) throw new Error('Supabase client not initialized');

    try {
      setLoadingStage('Building workflows.....');
      
      // First, check if user is a business owner (admin)
      const { data: ownedBusinesses, error: ownedBusinessError } = await supabase
        .from('businesses')
        .select('*')
        .eq('owner_id', userId)
        .order('created_at', { ascending: true });

      if (ownedBusinessError) {
        console.error('Error loading owned businesses:', ownedBusinessError);
        throw new Error('Failed to load business data');
      }

      // If user owns businesses, use the first one
      if (ownedBusinesses && ownedBusinesses.length > 0) {
        const selectedBusiness = ownedBusinesses[0];
        console.log('User is business owner, using business:', selectedBusiness.id);
        
        // Check subscription status for business owner (including trial)
        console.log('Checking subscription status for business:', selectedBusiness.id);
        console.log('Subscription status:', selectedBusiness.subscription_status);
        console.log('Trial enabled:', selectedBusiness.trial_enabled);
        console.log('Trial end date:', selectedBusiness.trial_end_date);
        
        const isBusinessActive = selectedBusiness.subscription_status === 'active' || 
          (selectedBusiness.trial_enabled && 
           selectedBusiness.trial_end_date && 
           new Date(selectedBusiness.trial_end_date) > new Date());
        
        if (!isBusinessActive) {
          console.log('❌ Subscription is inactive and no active trial, redirecting to subscription management');
          localStorage.setItem('selectedBusiness', JSON.stringify(selectedBusiness));
          localStorage.setItem('businesses', JSON.stringify(ownedBusinesses));
          navigate('/admin-subscription-management', { replace: true });
          return null;
        }
        
        // Store business data for quick access
        localStorage.setItem('selectedBusiness', JSON.stringify(selectedBusiness));
        localStorage.setItem('businesses', JSON.stringify(ownedBusinesses));
        
        // Continue with normal flow for business owners
        await preloadCalendarData(selectedBusiness);
        return await determineHomeScreen(selectedBusiness.id);
      }

      // If user doesn't own any businesses, check if they're a resource
      console.log('User is not a business owner, checking if they are a resource...');
      
      const { data: resourceData, error: resourceError } = await supabase
        .from('resources')
        .select(`
          *,
          businesses!inner (*)
        `)
        .eq('auth_user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: true });

      if (resourceError) {
        console.error('Error loading resource data:', resourceError);
        throw new Error('Failed to load resource data');
      }

      if (!resourceData || resourceData.length === 0) {
        console.log('User is not a resource either, redirecting to setup');
        return '/setup'; // User needs to be added to a business
      }

      // User is a resource, use their first active business
      const resource = resourceData[0];
      const selectedBusiness = resource.businesses;
      
      console.log('User is a resource for business:', selectedBusiness.id);
      console.log('Resource subscription status:', resource.subscription_status);
      console.log('Business subscription status:', selectedBusiness.subscription_status);
      
      // Check business subscription status first (including trial)
      const isBusinessActive = selectedBusiness.subscription_status === 'active' || 
        (selectedBusiness.trial_enabled && 
         selectedBusiness.trial_end_date && 
         new Date(selectedBusiness.trial_end_date) > new Date());
      
      if (!isBusinessActive) {
        console.log('❌ Business subscription is inactive and no active trial, redirecting resource to expired screen');
        localStorage.setItem('selectedBusiness', JSON.stringify(selectedBusiness));
        navigate('/resource-subscription-expired', { replace: true });
        return null;
      }
      
      // Check resource subscription status
      if (resource.subscription_status === 'inactive') {
        console.log('❌ Resource subscription is inactive, access denied');
        throw new Error('Your account access has been suspended. Please contact your administrator.');
      }
      
      // Store business data for quick access
      localStorage.setItem('selectedBusiness', JSON.stringify(selectedBusiness));
      localStorage.setItem('businesses', JSON.stringify([selectedBusiness]));
      
      // Continue with normal flow for resources
      await preloadCalendarData(selectedBusiness);
      return await determineHomeScreen(selectedBusiness.id);
    } catch (error) {
      console.error('Error during business/route loading:', error);
      // Return default route on error
      return '/calendarV2';
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setError(null);
    setValidationErrors({
      email: '',
      password: ''
    });
    setLoading(true);
    setLoadingStage('Waking up Nexus.....');

    if (!validateForm()) {
      setLoading(false);
      setLoadingStage('');
      return;
    }

    // Trigger login animation for both desktop and mobile
    setShowLoginAnimation(true);
    // Small delay to let animation start
    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Enhanced debugging for Android devices
      const isAndroid = /Android/.test(navigator.userAgent);
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    (window.navigator as any).standalone === true ||
                    document.referrer.includes('android-app://');
      
      console.log('🔍 Login attempt details:', {
        isAndroid,
        isPWA,
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        hasLocalStorage: typeof Storage !== 'undefined',
        hasIndexedDB: 'indexedDB' in window
      });

      // Set flag to prevent auth state change listener from interfering
      localStorage.setItem('enhancedLoginInProgress', 'true');
      
      // Add timeout for Android devices that might have slower connections
      const timeoutDuration = isAndroid ? 20000 : 10000; // Reduced from 30s/15s to 20s/10s
      
      const loginPromise = supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim()
      });
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Login timeout after ${timeoutDuration / 1000} seconds`));
        }, timeoutDuration);
      });
      
      // Race between login and timeout
      const { data, error } = await Promise.race([loginPromise, timeoutPromise]) as any;

      if (error) {
        // Clear the flag on error
        localStorage.removeItem('enhancedLoginInProgress');
        
        // Enhanced error logging for Android debugging
        console.error('🔍 Login error details:', {
          error: error.message,
          code: error.status,
          isAndroid,
          userAgent: navigator.userAgent
        });
        
        throw error;
      }

      if (!data.session || !data.user) {
        // Clear the flag on error
        localStorage.removeItem('enhancedLoginInProgress');
        throw new Error('No session returned from authentication');
      }

      console.log('✅ Authentication successful, checking setup status...');

      // Check if user has completed setup
      const { data: setupData } = await supabase
        .from('user_setup_status')
        .select('*')
        .eq('user_id', data.user.id)
        .single();

      if (setupData && !setupData.completed) {
        // User hasn't completed setup, redirect to setup flow
        console.log('⚠️ User setup not completed, redirecting to setup...');
        navigate('/setup', { 
          state: { 
            userId: data.user.id,
            email: email.trim(),
            message: 'Please complete your setup to continue.' 
          }
        });
        return;
      }

      console.log('✅ Setup completed, loading business data...');

      // Now do all the initialization work that would normally happen in AuthRedirectHandler
      const targetRoute = await loadBusinessesAndDetermineRoute(data.user.id);

      // If targetRoute is null, the user was redirected to subscription management
      if (targetRoute === null) {
        console.log('✅ User redirected to subscription management');
        return; // Exit early, navigation already handled
      }

      setLoadingStage('Analyzing Data.........');

      // Store the business data more permanently for the app to use
      const preloadedBusinessData = localStorage.getItem('selectedBusiness');
      const preloadedBusinesses = localStorage.getItem('businesses');
      
      if (preloadedBusinessData && preloadedBusinesses) {
        // Set a flag so BusinessContext knows to use this data immediately
        localStorage.setItem('usePreloadedBusinessData', 'true');
        
        // CRITICAL: Set the bypass flags that App.tsx routing expects
        localStorage.setItem('skipInitializing', 'true');
        localStorage.setItem('preloadedRoute', targetRoute);
      } else {
        // FALLBACK: If no preloaded data, BusinessContext will detect session via periodic checks
        console.log('🔄 No preloaded business data - BusinessContext will detect session automatically...');
      }

      // Allow time for UI components to settle - reduced from 1800ms to 500ms
      await new Promise(resolve => setTimeout(resolve, 500));

      // Handle Remember Me functionality
      // Removed remember me logic as per edit hint

      console.log('✅ Full initialization complete, navigating directly to:', targetRoute);

      // Navigate DIRECTLY to the target route, not to "/" 
      // This completely bypasses AuthRedirectHandler and loading screens
      // Note: The enhancedLoginInProgress flag will be cleared by the auth state change listener
      navigate(targetRoute, { replace: true });
      
    } catch (err) {
      console.error('Login error:', err);
      
      // Clear the flag on error
      localStorage.removeItem('enhancedLoginInProgress');
      
      let errorMessage = 'An error occurred during login';
      
      if (err instanceof Error) {
        if (err.message.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password';
          // Clear saved credentials if login fails with invalid credentials
          // Removed remember me logic as per edit hint
        } else if (err.message.includes('rate limit')) {
          errorMessage = 'Too many login attempts. Please wait a few minutes and try again.';
        } else if (err.message.includes('email not confirmed')) {
          errorMessage = 'Please verify your email address before logging in.';
        } else if (err.message.includes('Failed to load business data')) {
          errorMessage = 'Login successful, but failed to load your business data. Please try refreshing the page.';
        } else if (err.message.includes('No active subscription')) {
          errorMessage = 'No active subscription. Please contact your administrator to activate your subscription.';
        } else if (err.message.includes('account access has been suspended')) {
          errorMessage = 'Your account access has been suspended. Please contact your administrator.';
        }
      }

      setError(errorMessage);
      setPassword(''); // Clear password on error
    } finally {
      setLoading(false);
      setLoadingStage('');
    }
  };

  if (isLoading) {
    return <LoadingScreen message="Initializing Nexus....." />;
  }

  return (
    <div className="min-h-screen bg-black flex relative overflow-hidden" style={{
      paddingTop: 'env(safe-area-inset-top, 0px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }}>
      {/* Mobile Background Animation */}
      <div className="lg:hidden absolute inset-0 z-0 flex items-start justify-center pt-4">
        <div className="mobile-solar-system">
          {/* Mobile orbital rings */}
          <div className="mobile-orbit mobile-orbit-1"></div>
          <div className="mobile-orbit mobile-orbit-2"></div>
          <div className="mobile-orbit mobile-orbit-3"></div>
          
          {/* Mobile orbs */}
          <div className="mobile-orb mobile-orb-1"></div>
          <div className="mobile-orb mobile-orb-2"></div>
          <div className="mobile-orb mobile-orb-3"></div>
          <div className="mobile-orb mobile-orb-4"></div>
          <div className="mobile-orb mobile-orb-5"></div>
        </div>
      </div>

      {/* Desktop Login Animation Overlay */}
      {showLoginAnimation && (
        <div className="hidden lg:block fixed inset-0 z-50 bg-black">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="solar-system solar-system-centered">
              {/* Orbital rings */}
              <div className="orbit orbit-1"></div>
              <div className="orbit orbit-2"></div>
              <div className="orbit orbit-3"></div>
              <div className="orbit orbit-4"></div>
              
              {/* Positioned orbs */}
              <div className="orb orb-blue"></div>
              <div className="orb orb-purple"></div>
              <div className="orb orb-pink"></div>
              <div className="orb orb-blue-2"></div>
              <div className="orb orb-purple-2"></div>
              <div className="orb orb-pink-2"></div>
              <div className="orb orb-blue-3"></div>
              
              {/* Small particles */}
              <div className="particle particle-1"></div>
              <div className="particle particle-2"></div>
              <div className="particle particle-3"></div>
              <div className="particle particle-4"></div>
            </div>
          </div>
          
          {/* Morphing Loading Text */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="text-center">
              <div className="login-loading-text">
                {(loadingStage || 'Waking up Nexus.....').replace(/\.+$/, '')}
                <span className="animated-dots">
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Login Animation Overlay */}
      {showLoginAnimation && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="mobile-solar-system mobile-solar-system-centered">
              {/* Mobile orbital rings */}
              <div className="mobile-orbit mobile-orbit-1"></div>
              <div className="mobile-orbit mobile-orbit-2"></div>
              <div className="mobile-orbit mobile-orbit-3"></div>
              
              {/* Mobile orbs */}
              <div className="mobile-orb mobile-orb-1 mobile-orb-enhanced"></div>
              <div className="mobile-orb mobile-orb-2 mobile-orb-enhanced"></div>
              <div className="mobile-orb mobile-orb-3 mobile-orb-enhanced"></div>
              <div className="mobile-orb mobile-orb-4 mobile-orb-enhanced"></div>
              <div className="mobile-orb mobile-orb-5 mobile-orb-enhanced"></div>
            </div>
          </div>
          
          {/* Mobile Loading Text */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="text-center">
              <div className="mobile-loading-text">
                {(loadingStage || 'Waking up Nexus.....').replace(/\.+$/, '')}
                <span className="animated-dots">
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Left Side - Login Form */}
      <div className={`flex-1 flex items-center justify-center px-4 sm:px-8 lg:px-16 min-h-screen lg:min-h-0 relative z-10 transition-transform duration-700 ease-in-out ${
        showLoginAnimation ? 'lg:-translate-x-full' : ''
      }`}>
        <div className="w-full max-w-md space-y-6 sm:space-y-8 py-8 sm:py-0">
          {/* Welcome Header */}
          <div className="text-left">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Welcome!</h1>
            <p className="text-gray-400 text-base sm:text-lg">Log in to Nexus to continue to Nexus.</p>
          </div>

          {/* Social Login Buttons */}
          <div className="space-y-4">
            <button className="w-full flex items-center justify-center px-4 py-3 border border-gray-600 rounded-lg bg-transparent hover:bg-gray-800 text-white transition-all duration-200 ease-out">
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
              </svg>
              Log in with Apple
            </button>

            <button className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-900 transition-all duration-200 ease-out">
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Log in with Google
            </button>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-black text-gray-400">OR</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-white">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={`w-full px-4 py-3 bg-transparent border ${
                  validationErrors.email ? 'border-red-500' : 'border-gray-600'
                } rounded-lg text-white placeholder-gray-400 transition-all duration-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ fontSize: '16px' }}
              />
              {validationErrors.email && (
                <p className="text-red-400 text-sm">{validationErrors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-white">
                  Password
                </label>
                <Link
                  to="/reset-password"
                  className="text-sm text-gray-400 hover:text-white transition-colors underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  className={`w-full px-4 py-3 pr-12 bg-transparent border ${
                    validationErrors.password ? 'border-red-500' : 'border-gray-600'
                  } rounded-lg text-white placeholder-gray-400 transition-all duration-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ fontSize: '16px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {validationErrors.password && (
                <p className="text-red-400 text-sm">{validationErrors.password}</p>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
              style={{ minHeight: '44px' }}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-3">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>{loadingStage || 'Connecting...'}</span>
                </div>
              ) : (
                <span>Log in</span>
              )}
            </button>

            {/* Sign Up Link */}
            <div className="text-center pt-4">
              <p className="text-gray-400 text-sm">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  className="text-white hover:text-gray-300 transition-colors underline"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Right Side - Marketing Content */}
      <div className={`hidden lg:flex flex-1 items-center justify-center relative overflow-hidden bg-black transition-all duration-700 ease-in-out ${
        showLoginAnimation ? 'lg:translate-x-1/2 lg:scale-125' : ''
      }`}>
        {/* Orbital System - Exact Match */}
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <div className="solar-system">
            {/* Orbital rings */}
            <div className="orbit orbit-1"></div>
            <div className="orbit orbit-2"></div>
            <div className="orbit orbit-3"></div>
            <div className="orbit orbit-4"></div>
            
            {/* Positioned orbs */}
            <div className="orb orb-blue"></div>
            <div className="orb orb-purple"></div>
            <div className="orb orb-pink"></div>
            <div className="orb orb-blue-2"></div>
            <div className="orb orb-purple-2"></div>
            <div className="orb orb-pink-2"></div>
            <div className="orb orb-blue-3"></div>
            
            {/* Small particles */}
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            <div className="particle particle-4"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-20 text-center px-8 xl:px-12">
          <div className="text-6xl xl:text-7xl font-light mb-6 leading-tight tracking-wide text-gray-400" style={{ opacity: 0.5 }}>
            Complete business suite.
          </div>
          <div className="text-6xl xl:text-7xl font-light mb-6 leading-tight tracking-wide text-gray-400" style={{ opacity: 0.5 }}>
            AI-powered workflows.
          </div>
          <div className="text-6xl xl:text-7xl font-light leading-tight tracking-wide text-gray-400" style={{ opacity: 0.5 }}>
            Intelligent automation.
          </div>
        </div>
      </div>
    </div>
  );
}